import React from 'react';
import { createRoot } from 'react-dom/client';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { PatternPages, PAGE_W, PAGE_H } from '../preview/PreviewDocument';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

// Render a React element to a hidden DOM node and return it
function mountHidden(element) {
  const container = document.createElement('div');
  container.style.cssText = `
    position: fixed; left: -9999px; top: 0;
    width: ${PAGE_W}px; height: ${PAGE_H}px;
    overflow: hidden; z-index: -1;
  `;
  document.body.appendChild(container);
  const root = createRoot(container);
  root.render(element);
  return { container, root };
}

function cleanup({ container, root }) {
  root.unmount();
  document.body.removeChild(container);
}

async function waitForImages(container) {
  const imgs = Array.from(container.querySelectorAll('img'));
  if (!imgs.length) return;
  await Promise.all(
    imgs.map(img =>
      img.complete
        ? Promise.resolve()
        : new Promise(r => { img.onload = r; img.onerror = r; })
    )
  );
}

async function captureElement(element) {
  return html2canvas(element, {
    scale: 2.5,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    width: PAGE_W,
    height: PAGE_H,
    windowWidth: PAGE_W,
    windowHeight: PAGE_H,
    logging: false,
  });
}

// ─── Main PDF generation ──────────────────────────────────────────────────────

export async function generatePDF(pattern, options = {}) {
  const { format = 'a4', onProgress } = options;
  const pxW = format === 'a4' ? 595 : 612;
  const pxH = format === 'a4' ? 842 : 792;

  const pages = PatternPages({
    pattern,
    photos: pattern.photos || [],
    branding: pattern.branding || {},
  }) || [];

  if (!pages.length) throw new Error('No content to export');

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: [pxW, pxH],
  });

  for (let i = 0; i < pages.length; i++) {
    if (onProgress) onProgress(Math.round((i / pages.length) * 90));

    const { container, root } = mountHidden(pages[i]);

    // Wait for images to fully load
    await waitForImages(container);
    await new Promise(r => setTimeout(r, 500));

    const canvas = await captureElement(container.firstChild || container);
    cleanup({ container, root });

    if (i > 0) pdf.addPage([pxW, pxH], 'portrait');
    pdf.addImage(
      canvas.toDataURL('image/jpeg', 0.95),
      'JPEG',
      0, 0, pxW, pxH,
      undefined, 'FAST'
    );
  }

  if (onProgress) onProgress(100);
  return pdf;
}

export async function downloadPDF(pattern, options = {}) {
  const pdf = await generatePDF(pattern, options);
  const slug = (pattern.title || 'pattern').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  pdf.save(`${slug}-crochet-pattern.pdf`);
}

// ─── Watermarked PDF ──────────────────────────────────────────────────────────

export async function generateWatermarkedPDF(pattern, options = {}) {
  const pdf = await generatePDF(pattern, options);

  const pageCount = pdf.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setGState(new pdf.GState({ opacity: 0.12 }));
    pdf.setTextColor(150, 100, 68);
    pdf.setFontSize(42);
    pdf.setFont('helvetica', 'bold');

    const text = 'PREVIEW ONLY';
    const textW = pdf.getTextWidth(text);
    const pw = pdf.internal.pageSize.getWidth();
    const ph = pdf.internal.pageSize.getHeight();

    // Diagonal watermark ×2
    pdf.text(text, pw / 2 - textW / 2 - 20, ph / 2, { angle: 45 });
    pdf.text(text, pw / 2 - textW / 2 + 60, ph / 2 + 100, { angle: 45 });
  }

  return pdf;
}

// ─── Etsy ZIP package ─────────────────────────────────────────────────────────

export async function downloadEtsyZip(pattern, options = {}) {
  const { onProgress } = options;

  const zip = new JSZip();
  const slug = (pattern.title || 'pattern').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

  if (onProgress) onProgress(5);

  // 1. Main PDF
  const mainPdf = await generatePDF(pattern, {
    onProgress: p => onProgress && onProgress(5 + p * 0.45),
  });
  zip.file(`${slug}-crochet-pattern.pdf`, mainPdf.output('blob'));

  // 2. Watermarked preview PDF
  const previewPdf = await generateWatermarkedPDF(pattern, {
    onProgress: p => onProgress && onProgress(50 + p * 0.35),
  });
  zip.file(`${slug}-preview-watermarked.pdf`, previewPdf.output('blob'));

  if (onProgress) onProgress(88);

  // 3. Etsy listing text
  const listingText = buildEtsyListingText(pattern);
  zip.file('etsy-listing-text.txt', listingText);

  if (onProgress) onProgress(94);

  // 4. README
  zip.file('README.txt', `Thank you for purchasing this pattern!\n\n${pattern.title}\nBy ${pattern.branding?.shop_name || 'the designer'}\n\nFor personal use only. Do not redistribute.\n`);

  if (onProgress) onProgress(98);

  const content = await zip.generateAsync({ type: 'blob' });
  saveAs(content, `${slug}-etsy-package.zip`);

  if (onProgress) onProgress(100);
}

function buildEtsyListingText(pattern) {
  const d = pattern;
  const shop = d.branding?.shop_name || '';
  const ig = d.branding?.instagram ? `@${d.branding.instagram}` : '';

  return `=== ETSY LISTING TEXT ===
Generated by Pattern Studio

TITLE (copy-paste into Etsy):
${d.title}${d.subtitle ? ' – ' + d.subtitle : ''} Crochet Pattern PDF | ${d.difficulty ? d.difficulty.charAt(0).toUpperCase() + d.difficulty.slice(1) : ''} Amigurumi

DESCRIPTION (edit as needed):
${d.description || ''}

Difficulty: ${d.difficulty || ''}
Finished Size: ${d.finished_size || ''}
Language: ${d.language?.toUpperCase() || 'US'} crochet terms

This is a DIGITAL DOWNLOAD — no physical item will be shipped.
You will receive a PDF pattern instantly after purchase.

${shop ? `Pattern by ${shop}` : ''}${ig ? ` | ${ig}` : ''}

SUGGESTED TAGS (copy into Etsy tags, max 13):
crochet pattern, amigurumi pattern, ${d.animal_type?.toLowerCase() || 'stuffed animal'} crochet, ${d.difficulty || 'beginner'} crochet, amigurumi pdf, crochet toy pattern, stuffed animal pattern, diy crochet, instant download pattern, cute crochet, nursery decor crochet, handmade toy pattern, crochet gift

===========================
`;
}
